import axios from 'axios';
import {insertMessage, mergeMessages} from '@/utils/chatMessages';
import {
  CLIENTS_CHAT_MESSAGES_REQUEST,
  CLIENTS_CHAT_MESSAGES_RESET,
  CLIENTS_CHAT_MESSAGES_REQUEST_STARTED,
  CLIENTS_CHAT_MESSAGE_SEND_REQUEST,
  CLIENTS_CHAT_CONFIG_REQUEST,
  CLIENTS_CHAT_CONVERSATIONS_DETAILS_REQUEST,
  CLIENTS_CHAT_ATTACH_FILE,
  CLIENTS_CHAT_DELETE_ATTACHMENT,
  CLIENTS_CHAT_WEBSOCKET_RECEIVED,
  CLIENTS_CHAT_GET_SUGGESTION_REQUEST,
  CLIENTS_CHAT_GET_IMPROVED_TEXT,
  CLIENTS_CHAT_MESSAGES_READ_REQUEST,
  CLIENTS_CHAT_WEBSOCKET_MESSAGE_READ_RECEIVED,
  CLIENTS_CHAT_MESSAGE_EDIT_REQUEST,
  CLIENTS_CHAT_MESSAGE_DELETE_REQUEST, CLIENTS_CHAT_CLEAR_ATTACHMENTS,
  CLIENTS_CHAT_MESSAGE_RETRY_REQUEST,
} from '../actions/chat';

const state = {
  clientsChatWebSocketURL: null,
  clientsChatConversationChannel: null,
  clientsChatConversationId: '',
  clientsChatMessages: [],
  // Every /message/list request takes the next sequence number; only the highest one
  // applied so far may overwrite message fields, so a slow older response cannot
  // revert what a newer one already wrote. Both counters are monotonic for the life of
  // the session - a reset bumps the epoch instead, which discards responses that belong
  // to a conversation the user has already left.
  clientsChatMessagesSeq: 0,
  clientsChatMessagesAppliedSeq: 0,
  clientsChatMessagesEpoch: 0,
  clientsChatAttachedFiles: [],
  clientsChatChannels: [],
  clientsChat: {
    chatId: null,
    items: [],
    itemsErrorCode: null,
    itemsLoading: false,
    sendLoading: false,
    sendMessageError: false,
  },
};

const getters = {
  clientsChat: () => state.clientsChat,
  clientsChatWebSocketURL: () => state.clientsChatWebSocketURL,
  clientsChatConversationChannel: () => state.clientsChatConversationChannel,
  clientsChatConversationId: () => state.clientsChatConversationId,
  clientsChatMessages: () => state.clientsChatMessages,
  clientsChatAttachedFiles: () => state.clientsChatAttachedFiles,
  clientsChatChannels: () => state.clientsChatChannels,
};

const actions = {
  /**
   * Get chat config request (web socket url)
   * @returns {Promise<void>}
   */
  [CLIENTS_CHAT_CONFIG_REQUEST]({commit}) {
    return axios.post('/insurance/conversation/config/detail')
      .then((resp) => {
        commit(CLIENTS_CHAT_CONFIG_REQUEST, resp);
        return resp.data.connectUrl;
      });
  },
  /**
   * Get conversation details (channel and id) request
   * @returns {Promise<void>}
   */
  [CLIENTS_CHAT_CONVERSATIONS_DETAILS_REQUEST]({commit, state}, payload) {
    const epoch = state.clientsChatMessagesEpoch;
    return axios.post(
      '/insurance/conversation/detail',
      payload,
    )
      .then((resp) => {
        // A chat closed while this was in flight must not overwrite the conversation
        // the user has since opened.
        if (epoch === state.clientsChatMessagesEpoch) {
          commit(CLIENTS_CHAT_CONVERSATIONS_DETAILS_REQUEST, resp);
        }
        return resp.data;
      });
  },
  /**
   * API request for a page of chat message history. The result is merged into the
   * current list instead of replacing it, so messages pushed by the websocket while
   * the request was in flight survive.
   *
   * Callers are responsible for marking messages read - this action never does it,
   * so a multi-page sync or a scroll into history cannot mark the tail read.
   *
   * @param commit
   * @param state
   * @param payload {conversationId: string, lastNumber: number, ticketId: ?number}
   * @returns {Promise<{messages: Array, applied: boolean}>}
   */
  [CLIENTS_CHAT_MESSAGES_REQUEST]({commit, state}, payload) {
    const seq = state.clientsChatMessagesSeq + 1;
    const epoch = state.clientsChatMessagesEpoch;
    commit(CLIENTS_CHAT_MESSAGES_REQUEST_STARTED, seq);
    const {conversationId} = payload;
    return axios.post('/insurance/conversation/message/list', {
      conversationId,
      ticketId: payload.ticketId,
      to: payload.lastNumber,
    }).then((resp) => {
      const messages = resp.data.messages || [];
      // The chat was reset or re-opened for another conversation while this was in flight.
      if (epoch !== state.clientsChatMessagesEpoch
        || conversationId !== state.clientsChatConversationId) {
        return {messages: [], applied: false};
      }
      commit(CLIENTS_CHAT_MESSAGES_REQUEST, {
        messages,
        seq,
        fresh: seq > state.clientsChatMessagesAppliedSeq,
      });
      return {messages, applied: true};
    });
  },
  [CLIENTS_CHAT_MESSAGES_READ_REQUEST]({commit}, payload) {
    return axios.post('/insurance/conversation/message/mark-read', {
      conversationId: payload.conversationId,
    }).then(() => {
      commit(CLIENTS_CHAT_MESSAGES_READ_REQUEST);
    });
  },
  /**
   * Send message request
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   * @returns {Promise}
   */
  [CLIENTS_CHAT_MESSAGE_SEND_REQUEST]({commit, dispatch, state}, payload) {
    return axios.post('/insurance/conversation/message/send', {
      cdnFileIds: state.clientsChatAttachedFiles.map((file) => file.cdnFileId),
      conversationId: payload.conversationId,
      text: payload.message,
      ticketId: payload.ticketId,
      leadId: payload.leadId,
      channelType: payload.channelType,
      insuranceChannelId: payload.insuranceChannelId,
      subject: payload.subject,
      isTemplate: payload.isTemplate,
      template_type: payload.templateType,
    }).then((resp) => {
      commit(CLIENTS_CHAT_MESSAGE_SEND_REQUEST, resp.data);
      return resp.data;
    });
  },
  /**
   * Re-attempt delivery of a message that failed to send.
   *
   * The text and attachments are NOT resent from here — the backend re-delivers the stored message
   * in place, so the thread keeps one bubble instead of gaining a duplicate on every attempt. The
   * response carries the same messageId with its error cleared or replaced.
   *
   * @param payload {{conversationId: string, messageId: string, insuranceChannelId?: string}}
   */
  [CLIENTS_CHAT_MESSAGE_RETRY_REQUEST]({commit}, payload) {
    return axios.post('/insurance/conversation/message/retry', {
      conversationId: payload.conversationId,
      messageId: payload.messageId,
      insuranceChannelId: payload.insuranceChannelId,
    }).then((resp) => {
      commit(CLIENTS_CHAT_MESSAGE_RETRY_REQUEST, resp.data);
      return resp.data;
    });
  },
  [CLIENTS_CHAT_CLEAR_ATTACHMENTS]({commit}) {
    commit(CLIENTS_CHAT_CLEAR_ATTACHMENTS);
  },
  [CLIENTS_CHAT_MESSAGE_EDIT_REQUEST]({commit, dispatch, state}, payload) {
    return axios.post('/insurance/conversation/message/edit', payload)
      .then((resp) => {
        commit(CLIENTS_CHAT_MESSAGE_EDIT_REQUEST, resp.data.message.editMessageDetails);
      });
  },
  [CLIENTS_CHAT_MESSAGE_DELETE_REQUEST]({commit, dispatch, state}, messageId) {
    return axios.post('/insurance/conversation/message/delete', {
      conversationId: state.clientsChatConversationId,
      messageId,
    })
      .then(() => {
        commit(CLIENTS_CHAT_MESSAGE_DELETE_REQUEST, messageId);
      });
  },
  /**
   * Attach file to message
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<void>}
   */
  [CLIENTS_CHAT_ATTACH_FILE]({commit, dispatch}, payload) {
    // upload file Multipart/form-data
    const formData = new FormData();
    formData.append('file', payload.file);
    return axios.post('/insurance/conversation/file/save', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((resp) => {
      commit(CLIENTS_CHAT_ATTACH_FILE, resp.data);
      return resp.data;
    });
  },
  [CLIENTS_CHAT_GET_SUGGESTION_REQUEST]({commit, dispatch}, conversationId) {
    return axios.post(
      'insurance/conversation/ai/suggest/list',
      {
        conversationId,
      },
    )
      .then((resp) => resp.data.items?.[0]?.content ?? null);
  },
  [CLIENTS_CHAT_GET_IMPROVED_TEXT]({commit}, {conversationId, text}) {
    return axios.post(
      'insurance/ai/improve-writing',
      {
        conversationId,
        text,
      },
    )
      .then((resp) => resp.data.text);
  },
};

const mutations = {
  [CLIENTS_CHAT_CONFIG_REQUEST]: (state, resp) => {
    state.clientsChatWebSocketURL = resp.data.connectUrl;
  },
  [CLIENTS_CHAT_CONVERSATIONS_DETAILS_REQUEST]: (state, resp) => {
    state.clientsChatConversationChannel = resp.data.conversationChannel;
    state.clientsChatConversationId = resp.data.conversationId;
    state.clientsChatChannels = resp.data.channels || [];
  },
  [CLIENTS_CHAT_MESSAGES_REQUEST_STARTED]: (state, seq) => {
    state.clientsChatMessagesSeq = seq;
  },
  [CLIENTS_CHAT_MESSAGES_REQUEST]: (state, {messages, seq, fresh}) => {
    state.clientsChatMessages = mergeMessages(state.clientsChatMessages, messages, {fresh});
    if (fresh) {
      state.clientsChatMessagesAppliedSeq = seq;
    }
  },
  // Messages are only ever merged, so switching to another conversation has to clear
  // the previous one explicitly. Bumping the epoch also discards any request the
  // previous conversation still has in flight.
  [CLIENTS_CHAT_MESSAGES_RESET]: (state) => {
    state.clientsChatMessages = [];
    state.clientsChatMessagesEpoch += 1;
  },
  [CLIENTS_CHAT_MESSAGES_READ_REQUEST]: (state) => {
    state.clientsChatMessages = state.clientsChatMessages.map((message) => ({
      ...message,
      supportIsRead: true,
    }));
  },
  [CLIENTS_CHAT_MESSAGE_SEND_REQUEST]: (state) => {
    // Will clear attached files after sending message
    state.clientsChatAttachedFiles = [];
  },
  [CLIENTS_CHAT_ATTACH_FILE]: (state, data) => {
    state.clientsChatAttachedFiles.push(data);
  },
  [CLIENTS_CHAT_DELETE_ATTACHMENT]: (state, data) => {
    state.clientsChatAttachedFiles = state.clientsChatAttachedFiles.filter((file) => file.cdnFileId !== data.fileId);
  },
  [CLIENTS_CHAT_CLEAR_ATTACHMENTS]: (state) => {
    state.clientsChatAttachedFiles = [];
  },
  [CLIENTS_CHAT_WEBSOCKET_RECEIVED]: (state, data) => {
    state.clientsChatMessages = insertMessage(state.clientsChatMessages, data);
  },
  [CLIENTS_CHAT_WEBSOCKET_MESSAGE_READ_RECEIVED]: (state) => {
    state.clientsChatMessages = state.clientsChatMessages.map((message) => ({
      ...message,
      clientIsRead: true,
    }));
  },
  [CLIENTS_CHAT_MESSAGE_EDIT_REQUEST]: (state, data) => {
    state.clientsChatMessages = state.clientsChatMessages.map((message) => {
      if (message.messageId === data.messageId) {
        return {
          ...message,
          text: data.text,
        };
      }
      return message;
    });
  },
  [CLIENTS_CHAT_MESSAGE_DELETE_REQUEST]: (state, messageId) => {
    state.clientsChatMessages = state.clientsChatMessages.filter((message) => message.messageId !== messageId);
  },
  /**
   * A retry re-delivers the SAME message row, so patch it in place rather than appending — the
   * response's error is either gone (delivered) or the new classification of a second failure.
   *
   * `?? null` rather than deleting the key, deliberately: mergeMessages spreads `{...prev, ...next}`
   * so that fields the list endpoint omits survive a refresh. If a cleared error were absent here
   * instead of null, the next /message/list merge would keep the OLD error and the bubble would stay
   * red forever after a successful retry.
   */
  [CLIENTS_CHAT_MESSAGE_RETRY_REQUEST]: (state, data) => {
    const details = data?.message?.sendMessageDetails;
    if (!details?.messageId) return;
    state.clientsChatMessages = state.clientsChatMessages.map((message) => (
      message.messageId === details.messageId
        ? {...message, error: details.error ?? null}
        : message
    ));
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
